import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Users } from "lucide-react";

interface NomineesSectionProps {
  form: UseFormReturn<any>;
}

export const MembershipNomineesSection = ({ form }: NomineesSectionProps) => {
  return (
    <Card className="form-section-card animate-fade-in hover-scale">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          Nominated Beneficiaries
        </CardTitle>
        <CardDescription>
          Nominate your beneficiaries. If you need to nominate more than two, a separate form must be attached.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Nominee 1 - Required */}
        <div className="border rounded-lg p-6 space-y-6">
          <h4 className="font-semibold text-lg">Nominee 1 (Required)</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="nominee1FullNames"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Names (As they appear on ID) *</FormLabel>
                  <FormControl>
                    <Input {...field} className="form-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="nominee1DateOfBirth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date of Birth</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} className="form-input" />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="nominee1IdNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ID Number *</FormLabel>
                  <FormControl>
                    <Input {...field} className="form-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="nominee1Relationship"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Relationship</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Spouse, Child, Parent" {...field} className="form-input" />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="nominee1PostalAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Postal Address</FormLabel>
                  <FormControl>
                    <Input {...field} className="form-input" />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="nominee1Email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} className="form-input" />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="nominee1Phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telephone</FormLabel>
                  <FormControl>
                    <Input type="tel" {...field} className="form-input" />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="nominee1Percentage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Percentage Assigned *</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="1" 
                      max="100" 
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      className="form-input" 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Nominee 2 - Optional */}
        <div className="border rounded-lg p-6 space-y-6 bg-accent/10">
          <h4 className="font-semibold text-lg">Nominee 2 (Optional)</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="nominee2FullNames"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Names (As they appear on ID)</FormLabel>
                  <FormControl>
                    <Input {...field} className="form-input" />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="nominee2DateOfBirth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date of Birth</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} className="form-input" />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="nominee2IdNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ID Number</FormLabel>
                  <FormControl>
                    <Input {...field} className="form-input" />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="nominee2Relationship"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Relationship</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Spouse, Child, Parent" {...field} className="form-input" />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="nominee2PostalAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Postal Address</FormLabel>
                  <FormControl>
                    <Input {...field} className="form-input" />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="nominee2Email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} className="form-input" />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="nominee2Phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telephone</FormLabel>
                  <FormControl>
                    <Input type="tel" {...field} className="form-input" />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="nominee2Percentage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Percentage Assigned</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="0" 
                      max="100" 
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value) || 0)}
                      className="form-input" 
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800">
            <strong>Note:</strong> The total percentage assigned to all nominees must equal 100%. 
            If you have only one nominee, assign them 100%. If you have two nominees, 
            ensure their percentages add up to exactly 100%.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};